/**
 * Classifying passkey (WebAuthn) failures from supabase-js.
 *
 * Browser-side ceremony failures arrive as a WebAuthnError with a `code`
 * (and the browser's DOMException as `cause`); server rejections as an
 * AuthError whose `code` is Supabase's error_code. Both are matched by shape,
 * not class, so this also holds for raw DOMExceptions.
 */

const CANCELLATION_NAMES = new Set(['AbortError', 'NotAllowedError']);

const MESSAGES_BY_CODE: Record<string, string> = {
  // Server (Supabase Auth error_code)
  webauthn_credential_not_found:
    "That passkey isn't registered with Luminaria. It may have been removed — sign in with your password instead.",
  webauthn_verification_failed: "Couldn't verify the passkey. Please try again.",
  webauthn_challenge_expired: 'The passkey request expired. Please try again.',
  webauthn_credential_exists: 'This device already has a passkey for your account.',
  too_many_passkeys: "You've reached the maximum number of passkeys. Remove one to add another.",
  passkey_disabled: "Passkey sign-in isn't turned on for Luminaria yet.",
  // Browser (WebAuthnError code)
  ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED: 'This device already has a passkey for your account.',
  ERROR_INVALID_DOMAIN: "Passkeys can't be used on this site address.",
  ERROR_INVALID_RP_ID: "Passkeys can't be used on this site address.",
};

const FALLBACK_MESSAGE = 'Something went wrong with the passkey. Please try again.';

function field(value: unknown, key: string): unknown {
  return typeof value === 'object' && value !== null && key in value
    ? (value as Record<string, unknown>)[key]
    : undefined;
}

function errorCode(error: unknown): string | undefined {
  const code = field(error, 'code');
  return typeof code === 'string' ? code : undefined;
}

/**
 * The user dismissed the browser's prompt, or the ceremony was aborted
 * (superseded by another request, or the page moved on). Not worth a message.
 */
export function isPasskeyCancellation(error: unknown): boolean {
  if (errorCode(error) === 'ERROR_CEREMONY_ABORTED') return true;
  const names = [field(error, 'name'), field(field(error, 'cause'), 'name')];
  return names.some((name) => typeof name === 'string' && CANCELLATION_NAMES.has(name));
}

/** The server's challenge expired before the user finished; a fresh attempt will work. */
export function isExpiredPasskeyChallenge(error: unknown): boolean {
  return errorCode(error) === 'webauthn_challenge_expired';
}

/** A user-facing explanation, or null when the failure was a cancellation. */
export function passkeyErrorMessage(error: unknown): string | null {
  if (isPasskeyCancellation(error)) return null;
  const code = errorCode(error);
  if (code && MESSAGES_BY_CODE[code]) return MESSAGES_BY_CODE[code];
  return error instanceof Error && error.message ? error.message : FALLBACK_MESSAGE;
}
